/**
 * First Login Setup Page
 * New admins complete their account setup here
 */

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { tokenManager } from '../utils/tokenManager';
import { useAuth } from '../store/AuthContext';
import { allCountries } from '../data/countries';
import './FirstLoginSetupPage.css';

// Country calling codes mapping
const countryCallingCodes: { [key: string]: string } = {
  'GB': '+44', 'US': '+1', 'CA': '+1', 'AU': '+61', 'IE': '+353',
  'FR': '+33', 'DE': '+49', 'IT': '+39', 'ES': '+34', 'NL': '+31',
  'BE': '+32', 'AT': '+43', 'CH': '+41', 'SE': '+46', 'NO': '+47',
  'DK': '+45', 'FI': '+358', 'PL': '+48', 'PT': '+351', 'GR': '+30',
  'NZ': '+64', 'ZA': '+27', 'SG': '+65', 'HK': '+852', 'JP': '+81',
  'KR': '+82', 'CN': '+86', 'IN': '+91', 'BR': '+55', 'MX': '+52',
  'AR': '+54', 'CL': '+56', 'CO': '+57', 'PE': '+51', 'AE': '+971',
  'SA': '+966', 'IL': '+972', 'TR': '+90', 'RU': '+7', 'CZ': '+420',
  'HU': '+36', 'RO': '+40', 'BG': '+359', 'HR': '+385', 'SK': '+421',
  'SI': '+386', 'EE': '+372', 'LV': '+371', 'LT': '+370', 'IS': '+354',
  'LU': '+352', 'MT': '+356', 'CY': '+357', 'MY': '+60', 'TH': '+66',
  'ID': '+62', 'PH': '+63', 'VN': '+84', 'TW': '+886', 'EG': '+20',
  'MA': '+212', 'NG': '+234', 'KE': '+254', 'GH': '+233', 'TZ': '+255',
};

const FirstLoginSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { loadUser } = useAuth();
  
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Step 1: Password
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Step 2: PIN
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '', '', '']);
  const pinInputRef1 = useRef<HTMLInputElement>(null);
  const pinInputRef2 = useRef<HTMLInputElement>(null);
  const pinInputRef3 = useRef<HTMLInputElement>(null);
  const pinInputRef4 = useRef<HTMLInputElement>(null);
  const pinInputRef5 = useRef<HTMLInputElement>(null);
  const pinInputRef6 = useRef<HTMLInputElement>(null);
  const pinInputRefs = [pinInputRef1, pinInputRef2, pinInputRef3, pinInputRef4, pinInputRef5, pinInputRef6];
  const confirmPinInputRef1 = useRef<HTMLInputElement>(null);
  const confirmPinInputRef2 = useRef<HTMLInputElement>(null);
  const confirmPinInputRef3 = useRef<HTMLInputElement>(null);
  const confirmPinInputRef4 = useRef<HTMLInputElement>(null);
  const confirmPinInputRef5 = useRef<HTMLInputElement>(null);
  const confirmPinInputRef6 = useRef<HTMLInputElement>(null);
  const confirmPinInputRefs = [confirmPinInputRef1, confirmPinInputRef2, confirmPinInputRef3, confirmPinInputRef4, confirmPinInputRef5, confirmPinInputRef6];
  
  // Step 3: 2FA Method Selection
  const [twofaMethod, setTwofaMethod] = useState<'app' | 'sms'>('app');
  
  // Step 4: 2FA Setup
  const [twofaSecret, setTwofaSecret] = useState<string>('');
  const [qrCode, setQrCode] = useState<string>('');
  const [manualEntryKey, setManualEntryKey] = useState<string>('');
  const [twofaCode, setTwofaCode] = useState(['', '', '', '', '', '']);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState<string>('GB'); // Default to UK
  const [localPhoneNumber, setLocalPhoneNumber] = useState('');
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const codeInputRef1 = useRef<HTMLInputElement>(null);
  const codeInputRef2 = useRef<HTMLInputElement>(null);
  const codeInputRef3 = useRef<HTMLInputElement>(null);
  const codeInputRef4 = useRef<HTMLInputElement>(null);
  const codeInputRef5 = useRef<HTMLInputElement>(null);
  const codeInputRef6 = useRef<HTMLInputElement>(null);
  const codeInputRefs = [codeInputRef1, codeInputRef2, codeInputRef3, codeInputRef4, codeInputRef5, codeInputRef6];

  const handlePinChange = (index: number, value: string, currentPin: string[], setter: React.Dispatch<React.SetStateAction<string[]>>, refs: React.RefObject<HTMLInputElement>[]) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...currentPin];
    newPin[index] = value.slice(-1);
    setter(newPin);

    if (value && index < 5) {
      refs[index + 1].current?.focus();
    }
  };

  const handlePinKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
    currentPin: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    refs: React.RefObject<HTMLInputElement>[]
  ) => {
    // Handle Backspace/Delete
    if ((e.key === 'Backspace' || e.key === 'Delete') && !currentPin[index] && index > 0) {
      e.preventDefault();
      // Clear previous field and focus it
      const newPin = [...currentPin];
      newPin[index - 1] = '';
      setter(newPin);
      refs[index - 1].current?.focus();
    }
    // Handle Enter to submit form
    else if (e.key === 'Enter') {
      e.preventDefault();
      // Trigger form submit
      const form = e.currentTarget.closest('form');
      if (form) {
        form.requestSubmit();
      }
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...twofaCode];
    newCode[index] = value.slice(-1);
    setTwofaCode(newCode);

    if (value && index < 5) {
      codeInputRefs[index + 1].current?.focus();
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle Backspace/Delete
    if ((e.key === 'Backspace' || e.key === 'Delete') && !twofaCode[index] && index > 0) {
      e.preventDefault();
      // Clear previous field and focus it
      const newCode = [...twofaCode];
      newCode[index - 1] = '';
      setTwofaCode(newCode);
      codeInputRefs[index - 1].current?.focus();
    }
    // Handle Enter to verify
    else if (e.key === 'Enter') {
      e.preventDefault();
      handle2FAVerify();
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setCurrentStep(2);
  };

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!pin.every(d => d !== '')) {
      setError('Please enter a 6-digit PIN');
      return;
    }
    
    if (pin.join('') !== confirmPin.join('')) {
      setError('PINs do not match');
      return;
    }
    
    setCurrentStep(3);
  };

  const handle2FAMethodSelect = async (method: 'app' | 'sms') => {
    setError(null);
    setTwofaMethod(method);
    
    if (method === 'app') {
      // Start authenticator app setup
      setIsLoading(true);
      try {
        const result = await apiService.start2FASetup('app');
        setTwofaSecret(result.secret || '');
        setQrCode(result.qrCode || '');
        setManualEntryKey(result.manualEntryKey || '');
        setCurrentStep(4);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to start 2FA setup');
      } finally {
        setIsLoading(false);
      }
    } else if (method === 'sms') {
      // For SMS, we'll show phone input form, then proceed to step 4
      // Don't send SMS yet - wait for user to enter phone and click continue
    }
  };

  const handleSendSMSCode = async () => {
    if (!localPhoneNumber.trim()) {
      setError('Please enter your phone number');
      return;
    }
    
    const callingCode = countryCallingCodes[countryCode] || '+44';
    const fullPhoneNumber = `${callingCode}${localPhoneNumber.replace(/\D/g, '')}`;
    
    setIsLoading(true);
    setError(null);
    try {
      await apiService.start2FASetup('sms', fullPhoneNumber);
      setPhoneNumber(fullPhoneNumber);
      setSmsCodeSent(true);
      setCurrentStep(4);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send SMS code');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async () => {
    if (!twofaCode.every(d => d !== '')) {
      setError('Please enter the 6-digit code');
      return;
    }
    
    await completeSetup(twofaCode.join(''));
  };

  const completeSetup = async (twofaCodeValue?: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await apiService.completeFirstLogin({
        password,
        pin: pin.join(''),
        twofaMethod: twofaMethod,
        twofaSecret: twofaMethod === 'app' ? twofaSecret : undefined,
        twofaCode: twofaCodeValue,
        phoneNumber: twofaMethod === 'sms' ? (phoneNumber || (countryCallingCodes[countryCode] || '+44') + localPhoneNumber.replace(/\D/g, '')) : undefined,
      });
      
      // Save tokens immediately
      tokenManager.saveTokens(result.token, result.refreshToken, '');
      
      // Mark that first login was just completed to avoid race condition in loadUser()
      sessionStorage.setItem('first_login_just_completed', 'true');
      
      // Small delay to ensure token is saved in localStorage
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Now load the user - the sessionStorage flag will prevent checkFirstLoginStatus from running
      try {
        await loadUser();
        // Clear the flag after successful load
        sessionStorage.removeItem('first_login_just_completed');
        // Explicitly navigate to dashboard after successful setup
        setIsLoading(false);
        navigate('/dashboard');
      } catch (loadErr) {
        // If loadUser fails, clear flag and navigate anyway (token is saved)
        console.error('Failed to load user after first login setup:', loadErr);
        sessionStorage.removeItem('first_login_just_completed');
        setIsLoading(false);
        // Use window.location to force a full page reload with new token
        window.location.href = '/dashboard';
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete setup');
      setIsLoading(false);
    }
  };

  return (
    <div className="first-login-setup">
      <div className="setup-container">
        <div className="setup-card">
          <div className="setup-logo">
            <div className="setup-logo-icon">VS</div>
            <div className="setup-logo-text">VERSUS</div>
            <div className="setup-logo-sub">Complete Your Account Setup</div>
          </div>

          {error && (
            <div className="setup-error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Password */}
          {currentStep === 1 && (
            <div className="setup-step">
              <h2>Set Your Password</h2>
              <p>Choose a strong password for your admin account</p>
              <form onSubmit={handlePasswordSubmit}>
                <div className="setup-form-group">
                  <label>Password (min 8 characters)</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="setup-form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  Continue
                </button>
              </form>
            </div>
          )}

          {/* Step 2: PIN */}
          {currentStep === 2 && (
            <div className="setup-step">
              <h2>Set Your PIN</h2>
              <p>Enter a 6-digit PIN for action authorization</p>
              <form onSubmit={handlePinSubmit}>
                <div className="setup-form-group">
                  <label>PIN</label>
                  <div className="pin-input-group">
                    {pin.map((digit, index) => (
                      <input
                        key={index}
                        ref={pinInputRefs[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePinChange(index, e.target.value, pin, setPin, pinInputRefs)}
                        onKeyDown={(e) => handlePinKeyDown(index, e, pin, setPin, pinInputRefs)}
                        className="pin-input"
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                </div>
                <div className="setup-form-group">
                  <label>Confirm PIN</label>
                  <div className="pin-input-group">
                    {confirmPin.map((digit, index) => (
                      <input
                        key={index}
                        ref={confirmPinInputRefs[index]}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handlePinChange(index, e.target.value, confirmPin, setConfirmPin, confirmPinInputRefs)}
                        onKeyDown={(e) => handlePinKeyDown(index, e, confirmPin, setConfirmPin, confirmPinInputRefs)}
                        className="pin-input"
                        disabled={isLoading}
                      />
                    ))}
                  </div>
                </div>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  Continue
                </button>
              </form>
            </div>
          )}

          {/* Step 3: 2FA Method Selection */}
          {currentStep === 3 && (
            <div className="setup-step">
              <h2>Two-Factor Authentication</h2>
              <p>Choose your 2FA method (required)</p>
              <div className="twofa-options">
                <button
                  type="button"
                  className={`twofa-option ${twofaMethod === 'app' ? 'selected' : ''}`}
                  onClick={() => handle2FAMethodSelect('app')}
                  disabled={isLoading}
                >
                  <span className="twofa-option-icon">📱</span>
                  <div className="twofa-option-label">Authenticator App</div>
                </button>
                <button
                  type="button"
                  className={`twofa-option ${twofaMethod === 'sms' ? 'selected' : ''}`}
                  onClick={() => handle2FAMethodSelect('sms')}
                  disabled={isLoading}
                >
                  <span className="twofa-option-icon">💬</span>
                  <div className="twofa-option-label">SMS</div>
                </button>
              </div>

              {twofaMethod === 'sms' && !smsCodeSent && (
                <div style={{ marginTop: '24px' }}>
                  <div className="setup-form-group">
                    <label className="form-label">Phone Number</label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}>
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="country-code-select"
                        disabled={isLoading}
                        style={{
                          padding: '12px 16px',
                          background: 'var(--glass-highlight)',
                          border: '2px solid var(--glass-border)',
                          borderRadius: 'var(--radius-sm)',
                          color: 'var(--text)',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          minWidth: '120px',
                          appearance: 'none',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23ffffff' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 16px center',
                          paddingRight: '40px',
                        }}
                      >
                        {allCountries.map((country) => {
                          const callingCode = countryCallingCodes[country.code] || '+44';
                          return (
                            <option key={country.code} value={country.code}>
                              {country.flag} {callingCode}
                            </option>
                          );
                        })}
                      </select>
                      <input
                        type="tel"
                        value={localPhoneNumber}
                        onChange={(e) => setLocalPhoneNumber(e.target.value.replace(/\D/g, ''))}
                        placeholder="Phone number"
                        className="form-input"
                        disabled={isLoading}
                        style={{ flex: 1 }}
                        inputMode="numeric"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    className="button-primary"
                    onClick={handleSendSMSCode}
                    disabled={isLoading || !localPhoneNumber.trim()}
                    style={{ width: '100%', marginTop: '16px' }}
                  >
                    {isLoading ? 'Sending...' : 'Send SMS Code →'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 4: 2FA Setup */}
          {currentStep === 4 && (
            <div className="setup-step">
              <h2>Verify {twofaMethod === 'app' ? 'Authenticator App' : 'SMS'}</h2>
              
              {twofaMethod === 'app' && qrCode && (
                <div className="qr-code-container">
                  <img src={qrCode} alt="QR Code" />
                  <p>Scan this QR code with your authenticator app</p>
                  {manualEntryKey && (
                    <p className="manual-entry">Or enter manually: <code>{manualEntryKey}</code></p>
                  )}
                </div>
              )}
              
              {twofaMethod === 'sms' && smsCodeSent && (
                <div className="sms-sent">
                  <p>✅ SMS code sent to {phoneNumber}</p>
                </div>
              )}
              
              <div className="setup-form-group">
                <label>Enter 6-digit code</label>
                <div className="code-input-group">
                  {twofaCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={codeInputRefs[index]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      className="code-input"
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>
              
              <button
                onClick={handle2FAVerify}
                className="btn btn-primary"
                disabled={isLoading || !twofaCode.every(d => d !== '')}
              >
                {isLoading ? 'Verifying...' : 'Verify & Complete Setup'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FirstLoginSetupPage;
